/**
 * @fileoverview Página responsável por gerenciar filas de atendimento.
 */

import React, { useMemo, useState } from "react";

import {
  Button,
  IconButton,
  makeStyles,
  Typography,
  Box,
  Paper,
} from "@material-ui/core";
import {
  Forum as ChatIcon,
} from "@material-ui/icons";
import { Pencil, Plus, Trash2 as Trash } from "lucide-react";

import MainContainer from "../../components/MainContainer";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import DeleteQueueModal from "../../components/DeleteQueueModal";
import { useQueues } from "../../context/Queues/QueuesContext";

const GRID_COLUMNS = "72px minmax(260px, 1.4fr) minmax(220px, 1.2fr) 128px 96px";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(4),
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.mode === "light" ? "#1e293b" : "#FFFFFF",
  },
  buttonsWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  addButton: {
    whiteSpace: "nowrap",
    flexShrink: 0,
    height: 36,
    padding: "0 14px",
    borderRadius: 10,
    fontSize: "0.875rem",
    fontWeight: 500,
    textTransform: "none",
    gap: 8,
    border: "none",
    boxShadow: "none",
    background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
    color: "#ffffff",
    transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      boxShadow: "none",
    },
    "& svg": { flexShrink: 0 },
  },
  mainPaper: {
    overflowX: "auto",
    overflowY: "auto",
    backgroundColor: "transparent",
    boxShadow: "none",
    ...theme.scrollbarStyles,
  },
  table: {
    minWidth: 900,
  },
  listHeader: {
    display: "grid",
    gridTemplateColumns: GRID_COLUMNS,
    alignItems: "center",
    padding: "12px 20px",
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(0, 0, 0, 0.08)",
  },
  listHeaderText: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.45)"
        : "#94a3b8",
  },
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginTop: theme.spacing(1),
  },
  listRow: {
    display: "grid",
    gridTemplateColumns: GRID_COLUMNS,
    alignItems: "center",
    padding: "14px 20px",
    borderRadius: "12px",
    transition: "background 0.15s ease",
    cursor: "default",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(91, 124, 255, 0.06)"
          : "rgba(61, 92, 253, 0.04)",
    },
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.04)"
        : "1px solid rgba(0, 0, 0, 0.04)",
  },
  queueId: {
    fontSize: "0.8rem",
    fontWeight: 500,
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.4)"
        : "#94a3b8",
    fontVariantNumeric: "tabular-nums",
  },
  queueCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  queueIcon: {
    fontSize: "1.4rem",
    flexShrink: 0,
  },
  queueNameBlock: {
    minWidth: 0,
  },
  queueName: {
    fontSize: "0.98rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#1e293b",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  queueMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    minWidth: 0,
  },
  colorBadge: {
    width: 38,
    height: 14,
    borderRadius: 999,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.15)"
        : "1px solid rgba(0, 0, 0, 0.12)",
    flexShrink: 0,
  },
  colorText: {
    fontSize: "0.8rem",
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.5)"
        : "#64748b",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  greetingText: {
    fontSize: "0.86rem",
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.65)"
        : "#475569",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  orderBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 44,
    padding: "4px 12px",
    borderRadius: "8px",
    background:
      theme.palette.type === "dark"
        ? "rgba(139, 92, 246, 0.12)"
        : "rgba(139, 92, 246, 0.08)",
    color: theme.palette.type === "dark" ? "#c4b5fd" : "#7c3aed",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(139, 92, 246, 0.2)"
        : "1px solid rgba(139, 92, 246, 0.15)",
    fontSize: "0.8rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  actionsCell: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
  },
  iconButton: {
    padding: 6,
    borderRadius: "8px",
    transition: "all 0.15s ease",
  },
  editButton: {
    color: theme.palette.type === "dark" ? "#93c5fd" : "#3b82f6",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(59, 130, 246, 0.15)"
          : "rgba(59, 130, 246, 0.08)",
    },
  },
  deleteButton: {
    color: theme.palette.type === "dark" ? "#fca5a5" : "#ef4444",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(239, 68, 68, 0.15)"
          : "rgba(239, 68, 68, 0.08)",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6, 2),
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.35)"
        : "#94a3b8",
    fontSize: "0.95rem",
  },
}));

const Queues = () => {
  const classes = useStyles();

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ticketsCount, setTicketsCount] = useState(0);

  const { queues, refetchQueues } = useQueues();

  const sortedQueues = useMemo(
    () => [...queues].sort((a, b) => a.id - b.id),
    [queues]
  );

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedQueue(null);
    setTicketsCount(0);
  };

  const handleOpenDeleteModal = (queue) => {
    setSelectedQueue(queue);
    setConfirmModalOpen(true);
  };

  const handleConfirmDeleteQueue = async (queueToDelete) => {
    if (!queueToDelete) {
      return;
    }

    try {
      await api.delete(`/queue/${queueToDelete.id}`);
      toast.success(i18n.t("queues.toasts.success"));
      await refetchQueues();
      setSelectedQueue(null);
    } catch (err) {
      if (err.response?.data?.message === "ERR_QUEUE_HAS_TICKETS") {
        const count = err.response?.data?.ticketsCount || 0;
        setSelectedQueue(queueToDelete);
        setTicketsCount(count);
        setDeleteModalOpen(true);
      } else {
        toastError(err);
        setSelectedQueue(null);
      }
    }
  };

  const handleDeleteQueue = async (targetQueueId) => {
    if (!selectedQueue) {
      return;
    }

    try {
      await api.delete(`/queue/${selectedQueue.id}`, {
        data: { targetQueueId },
      });
      toast.success(i18n.t("queues.toasts.success"));
      await refetchQueues();
      handleCloseDeleteModal();
    } catch (err) {
      toastError(err);
    }
  };

  const getOrderLabel = (queue) => {
    if (
      queue.orderQueue === null ||
      queue.orderQueue === undefined ||
      queue.orderQueue === ""
    ) {
      return "-";
    }

    return queue.orderQueue;
  };

  return (
    <MainContainer className={classes.root}>
      <DeleteQueueModal
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        queue={selectedQueue}
        queues={queues}
        onConfirm={handleDeleteQueue}
        ticketsCount={ticketsCount}
      />

      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleConfirmDeleteQueue(selectedQueue)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
      />

      <div className={classes.header}>
        <Typography className={classes.title}>
          {i18n.t("queues.title")}
        </Typography>

        <div className={classes.buttonsWrapper}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Plus size={15} strokeWidth={1.75} />}
            onClick={handleOpenQueueModal}
            data-onboarding-tour="sectorsQueues"
            className={classes.addButton}
          >
            {i18n.t("queues.buttons.add")}
          </Button>
        </div>
      </div>

      <Paper className={classes.mainPaper}>
        <div className={classes.table}>
          <div className={classes.listHeader}>
            <Typography className={classes.listHeaderText}>
              {i18n.t("queues.table.id")}
            </Typography>
            <Typography className={classes.listHeaderText}>
              {i18n.t("queues.table.name")}
            </Typography>
            <Typography className={classes.listHeaderText}>
              {i18n.t("queues.table.greeting")}
            </Typography>
            <Typography className={classes.listHeaderText}>
              {i18n.t("queues.table.orderQueue")}
            </Typography>
            <Typography
              className={classes.listHeaderText}
              style={{ textAlign: "right" }}
            >
              {i18n.t("queues.table.actions")}
            </Typography>
          </div>

          <div className={classes.listContainer}>
            {sortedQueues.length === 0 ? (
              <Typography className={classes.emptyState}>
                {i18n.t("queues.emptyState")}
              </Typography>
            ) : (
              sortedQueues.map((queue) => (
                <div key={queue.id} className={classes.listRow}>
                  <Typography className={classes.queueId}>
                    #{queue.id}
                  </Typography>

                  <div className={classes.queueCell}>
                    <ChatIcon
                      className={classes.queueIcon}
                      style={{ color: queue.color }}
                    />

                    <div className={classes.queueNameBlock}>
                      <Typography className={classes.queueName}>
                        {queue.name}
                      </Typography>

                      <div className={classes.queueMeta}>
                        <Box
                          className={classes.colorBadge}
                          style={{ backgroundColor: queue.color }}
                        />
                        <Typography className={classes.colorText}>
                          {queue.color}
                        </Typography>
                      </div>
                    </div>
                  </div>

                  <Typography className={classes.greetingText}>
                    {queue.greetingMessage || "-"}
                  </Typography>

                  <div>
                    <span className={classes.orderBadge}>
                      {getOrderLabel(queue)}
                    </span>
                  </div>

                  <div className={classes.actionsCell}>
                    <IconButton
                      size="small"
                      className={`${classes.iconButton} ${classes.editButton}`}
                      onClick={() => handleEditQueue(queue)}
                      title="Editar"
                    >
                      <Pencil size={16} strokeWidth={1.5} />
                    </IconButton>
                    <IconButton
                      size="small"
                      className={`${classes.iconButton} ${classes.deleteButton}`}
                      onClick={() => handleOpenDeleteModal(queue)}
                      title="Excluir"
                    >
                      <Trash size={16} strokeWidth={1.5} />
                    </IconButton>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Queues;
